import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, XIcon, InfoIcon } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'info' | 'warning';
interface ToastProps {
  message: string;
  type: ToastType;
  duration?: number;
  onClose: () => void;
}
export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    // Start the timer for auto-dismissal
    const dismissTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade-out animation
    }, duration);
    // Progress bar animation
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - elapsed / duration * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 16); // ~60fps
    // Clean up timers
    return () => {
      clearTimeout(dismissTimer);
      clearInterval(interval);
    };
  }, [duration, onClose]);
  // Get icon and colors based on type
  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-12421" />,
          bgClass: 'bg-green-50 dark:bg-green-900/20',
          borderClass: 'border-green-500',
          progressClass: 'bg-green-500'
        };
      case 'error':
        return {
          icon: <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-12422" />,
          bgClass: 'bg-red-50 dark:bg-red-900/20',
          borderClass: 'border-red-500',
          progressClass: 'bg-red-500'
        };
      case 'warning':
        return {
          icon: <AlertCircleIcon className="h-5 w-5 text-amber-500" data-id="element-12423" />,
          bgClass: 'bg-amber-50 dark:bg-amber-900/20',
          borderClass: 'border-amber-500',
          progressClass: 'bg-amber-500'
        };
      case 'info':
      default:
        return {
          icon: <InfoIcon className="h-5 w-5 text-blue-500" data-id="element-12424" />,
          bgClass: 'bg-blue-50 dark:bg-blue-900/20',
          borderClass: 'border-blue-500',
          progressClass: 'bg-blue-500'
        };
    }
  };
  const styles = getToastStyles();
  if (!isVisible) return null;
  return <div className={`max-w-md w-full ${styles.bgClass} border-l-4 ${styles.borderClass} rounded-md shadow-lg pointer-events-auto transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`} role="alert" data-id="element-12425">
      <div className="flex p-4" data-id="element-12426">
        <div className="flex-shrink-0 mr-3" data-id="element-12427">{styles.icon}</div>
        <div className="flex-1 pt-0.5" data-id="element-12428">
          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-12429">
            {message}
          </p>
        </div>
        <button onClick={() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }} className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white focus:outline-none" data-id="element-12430">
          <XIcon className="h-5 w-5" data-id="element-12431" />
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-1 w-full bg-gray-200 dark:bg-gray-700" data-id="element-12432">
        <div className={`h-full ${styles.progressClass} transition-all duration-100 ease-linear`} style={{
        width: `${progress}%`
      }} data-id="element-12433"></div>
      </div>
    </div>;
};